import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter,
  Plus, Clock, AlertCircle,
  ArrowRight, CheckCircle2, Loader2,
  XCircle
} from 'lucide-react';
import { productCreationRequestService } from '../../../../services/erpApi';
import { Toast } from '../../../../components';
import { useToast } from '../../../../hooks/useToast';
import './ProductCreationRequestDashboard.css';

interface CreationRequest {
  id: number;
  request_number: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  payload?: any;
  created_by?: number;
  created_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  created_at: string;
  product_id?: number;
}

interface WorkflowStep {
  label: string;
  count: number;
  icon: any;
  color: string;
  bg: string;
}

const parsePayload = (payload: any): Record<string, any> => {
  if (!payload) return {};
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload;
};

const ProductCreationRequestDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [requests, setRequests] = useState<CreationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([
    { label: 'Pendentes', count: 0, icon: Clock, color: 'step-amber', bg: '' },
    { label: 'Aprovadas', count: 0, icon: CheckCircle2, color: 'step-emerald', bg: '' },
    { label: 'Rejeitadas', count: 0, icon: XCircle, color: 'step-rose', bg: '' },
  ]);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const [pendingResponse, approvedResponse, rejectedResponse] = await Promise.all([
        productCreationRequestService.list({ status: 'pending', limit: 1000 }),
        productCreationRequestService.list({ status: 'approved', limit: 1000 }),
        productCreationRequestService.list({ status: 'rejected', limit: 1000 })
      ]);

      const allRequests: CreationRequest[] = [
        ...(pendingResponse.success ? pendingResponse.data || [] : []),
        ...(approvedResponse.success ? approvedResponse.data || [] : []),
        ...(rejectedResponse.success ? rejectedResponse.data || [] : [])
      ];

      setRequests(allRequests);
      updateWorkflowCounts(allRequests);
    } catch (error: any) {
      console.error('Erro ao carregar solicitações:', error);
      showToast('Erro ao carregar solicitações de criação', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const updateWorkflowCounts = (list: CreationRequest[]) => {
    setWorkflowSteps([
      { label: 'Pendentes', count: list.filter(r => r.status === 'pending').length, icon: Clock, color: 'step-amber', bg: '' },
      { label: 'Aprovadas', count: list.filter(r => r.status === 'approved').length, icon: CheckCircle2, color: 'step-emerald', bg: '' },
      { label: 'Rejeitadas', count: list.filter(r => r.status === 'rejected').length, icon: XCircle, color: 'step-rose', bg: '' },
    ]);
  };

  const getStatusConfig = (request: CreationRequest) => {
    switch (request.status) {
      case 'pending': return { label: 'Pendente', color: 'status-pending', icon: Clock };
      case 'approved': return { label: 'Aprovada', color: 'status-approved', icon: CheckCircle2 };
      case 'rejected': return { label: 'Rejeitada', color: 'status-rejected', icon: XCircle };
      case 'cancelled': return { label: 'Cancelada', color: 'status-cancelled', icon: XCircle };
      default: return { label: 'Desconhecido', color: 'status-default', icon: AlertCircle };
    }
  };

  const handleRowClick = (request: CreationRequest) => {
    navigate(`/erp/produtos/solicitacoes/${request.id}`);
  };

  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const payload = parsePayload(request.payload);
    return (
      request.request_number?.toLowerCase().includes(search) ||
      request.id.toString().includes(search) ||
      request.created_by_name?.toLowerCase().includes(search) ||
      payload.sku?.toLowerCase().includes(search) ||
      payload.name?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Agora';
    if (hours < 24) return `Há ${hours}h`;
    if (days === 1) return 'Ontem';
    return `Há ${days}d`;
  };

  return (
    <div className="product-creation-dashboard">
      <div className="product-creation-dashboard-header">
        <div>
          <h1 className="product-creation-dashboard-title">
            Solicitações de <span className="product-creation-dashboard-title-accent">Criação</span>
          </h1>
          <p className="product-creation-dashboard-subtitle">
            Aprovação de novos produtos antes do cadastro
          </p>
        </div>
        <div className="product-creation-dashboard-actions">
          <button className="product-creation-dashboard-btn-secondary">
            <Filter size={16} /> Filtros
          </button>
          <button
            className="product-creation-dashboard-btn-primary"
            onClick={() => navigate('/erp/produtos/solicitacoes/nova')}
          >
            <Plus size={16} /> Nova Solicitação
          </button>
        </div>
      </div>

      <div className="product-creation-dashboard-workflow">
        <div className="product-creation-dashboard-workflow-header">
          <h3>
            <Clock size={16} />
            Status das Solicitações
          </h3>
        </div>
        <div className="product-creation-dashboard-workflow-grid">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={idx}
                className={`product-creation-dashboard-workflow-step ${step.color}`}
              >
                <div className="product-creation-dashboard-workflow-icon">
                  <Icon size={24} />
                  {step.count > 0 && (
                    <div className="product-creation-dashboard-workflow-badge">
                      {step.count}
                    </div>
                  )}
                </div>
                <p className="product-creation-dashboard-workflow-label">{step.label}</p>
                {idx < workflowSteps.length - 1 && (
                  <div className="product-creation-dashboard-workflow-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="product-creation-dashboard-card">
        <div className="product-creation-dashboard-card-header">
          <h3 className="product-creation-dashboard-card-title">Solicitações de Criação</h3>
          <div className="product-creation-dashboard-search-wrapper">
            <div className="product-creation-dashboard-search">
              <Search className="product-creation-dashboard-search-icon" size={14} />
              <input
                type="text"
                placeholder="Buscar por número, SKU, nome ou criador..."
                className="product-creation-dashboard-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="product-creation-dashboard-loading">
            <Loader2 size={24} className="product-creation-dashboard-spinner" />
            <p>Carregando...</p>
          </div>
        ) : (
          <div className="product-creation-dashboard-table-wrapper">
            <table className="product-creation-dashboard-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>SKU</th>
                  <th>Nome</th>
                  <th>Criado por</th>
                  <th className="text-center">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="product-creation-dashboard-empty">
                      <p>Nenhuma solicitação encontrada</p>
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => {
                    const config = getStatusConfig(request);
                    const Icon = config.icon;
                    const payload = parsePayload(request.payload);
                    return (
                      <tr
                        key={request.id}
                        className="product-creation-dashboard-table-row"
                        onClick={() => handleRowClick(request)}
                      >
                        <td>
                          <div className="product-creation-dashboard-invoice-info">
                            <span className="product-creation-dashboard-invoice-id">
                              {request.request_number}
                            </span>
                            <span className="product-creation-dashboard-invoice-number">
                              <CheckCircle2 size={10} /> ID: {request.id}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="product-creation-dashboard-supplier-name">
                            {payload.sku || '—'}
                          </span>
                        </td>
                        <td>
                          <span className="product-creation-dashboard-supplier-name">
                            {payload.name || '—'}
                          </span>
                        </td>
                        <td>
                          <span className="product-creation-dashboard-supplier-name">
                            {request.created_by_name || `Usuário #${request.created_by}`}
                          </span>
                          <p className="product-creation-dashboard-date">
                            <Clock size={10} /> {formatDate(request.created_at)}
                          </p>
                        </td>
                        <td>
                          <div className="product-creation-dashboard-status-wrapper">
                            <span className={`product-creation-dashboard-status ${config.color}`}>
                              <Icon size={12} />
                              {config.label}
                            </span>
                          </div>
                        </td>
                        <td className="text-right">
                          <button
                            className="product-creation-dashboard-btn-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(request);
                            }}
                          >
                            Ver <ArrowRight size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default ProductCreationRequestDashboard;
